<template>
  <div class="p-4 md:p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-5">
      <div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Tentativi di registrazione</h2>
        <p class="text-sm text-gray-500 mt-0.5">
          Aggregati per IP · solo IP con ≥ {{ filters.min_attempts }} tentativi
        </p>
      </div>
      <button
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-white text-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 text-sm transition"
        :disabled="loading"
        @click="refresh"
      >
        <span class="material-icons text-sm" :class="{ 'animate-spin': loading }">refresh</span>
        Aggiorna
      </button>
    </div>

    <!-- Filtri -->
    <div class="flex flex-wrap gap-3 mb-4">
      <div class="flex items-center gap-2">
        <span class="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Finestra:</span>
        <select
          v-model="filters.window_hours"
          class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="refresh"
        >
          <option :value="24">Ultime 24h</option>
          <option :value="72">Ultimi 3 giorni</option>
          <option :value="168">Ultima settimana</option>
          <option :value="720">Ultimo mese</option>
        </select>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Min. tentativi:</span>
        <select
          v-model="filters.min_attempts"
          class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="refresh"
        >
          <option :value="1">≥ 1</option>
          <option :value="2">≥ 2</option>
          <option :value="3">≥ 3</option>
          <option :value="5">≥ 5</option>
          <option :value="10">≥ 10</option>
        </select>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="space-y-2">
      <div
        v-for="i in 6"
        :key="i"
        class="h-12 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"
      ></div>
    </div>

    <!-- Empty -->
    <div
      v-else-if="!attempts.length"
      class="bg-white dark:bg-gray-900 rounded-xl shadow p-12 text-center text-gray-400"
    >
      <span class="material-icons text-5xl mb-3 block">manage_search</span>
      <p class="text-lg font-medium">Nessun tentativo sospetto trovato.</p>
      <p class="text-sm mt-1">Prova ad allargare la finestra temporale o abbassare la soglia minima.</p>
    </div>

    <!-- Tabella -->
    <div v-else class="bg-white dark:bg-gray-900 rounded-xl shadow overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-500 uppercase">
            <th class="px-4 py-2 text-left">IP</th>
            <th class="px-4 py-2 text-center">Tentativi</th>
            <th class="px-4 py-2 text-center hidden sm:table-cell">FOREIGN</th>
            <th class="px-4 py-2 text-left hidden md:table-cell">Profili</th>
            <th class="px-4 py-2 text-center hidden lg:table-cell">Successi</th>
            <th class="px-4 py-2 text-center hidden lg:table-cell">Bloccati</th>
            <th class="px-4 py-2 text-left hidden xl:table-cell">Primo</th>
            <th class="px-4 py-2 text-left">Ultimo</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
          <tr
            v-for="(row, i) in attempts"
            :key="i"
            class="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition"
          >
            <!-- IP -->
            <td class="px-4 py-3 font-mono font-semibold text-gray-900 dark:text-white text-xs">
              {{ row.ip }}
            </td>

            <!-- Tentativi badge -->
            <td class="px-4 py-3 text-center">
              <span
                class="inline-block px-2 py-0.5 rounded-full text-xs font-bold text-white"
                :class="attemptsBadgeColor(row.total_attempts)"
              >
                {{ row.total_attempts }}
              </span>
            </td>

            <!-- FOREIGN count -->
            <td class="px-4 py-3 text-center hidden sm:table-cell">
              <span v-if="row.foreign_attempts > 0" class="text-blue-600 dark:text-blue-400 font-medium">
                {{ row.foreign_attempts }}
              </span>
              <span v-else class="text-gray-300">—</span>
            </td>

            <!-- Profili chips -->
            <td class="px-4 py-3 hidden md:table-cell">
              <div class="flex gap-1 flex-wrap">
                <span
                  v-for="pt in parseProfileTypes(row.profile_types)"
                  :key="pt"
                  class="px-1.5 py-0.5 rounded text-xs"
                  :class="profileChipClass(pt)"
                >
                  {{ pt }}
                </span>
              </div>
            </td>

            <!-- Successi -->
            <td class="px-4 py-3 text-center hidden lg:table-cell">
              <span :class="row.success_count > 0 ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-300'">
                {{ row.success_count || '—' }}
              </span>
            </td>

            <!-- Bloccati -->
            <td class="px-4 py-3 text-center hidden lg:table-cell">
              <span :class="row.blocked_count > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-300'">
                {{ row.blocked_count || '—' }}
              </span>
            </td>

            <!-- Primo tentativo -->
            <td class="px-4 py-3 text-gray-400 text-xs hidden xl:table-cell">
              {{ relativeDate(row.first_attempt_at) }}
            </td>

            <!-- Ultimo tentativo -->
            <td class="px-4 py-3 text-gray-500 text-xs">
              {{ relativeDate(row.last_attempt_at) }}
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Footer count -->
      <div class="px-4 py-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400">
        {{ attempts.length }} IP distint{{ attempts.length === 1 ? 'o' : 'i' }}
        <span v-if="attempts.length >= 50"> · massimo 50 risultati, allarga la soglia se necessario</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      filters: {
        window_hours: 72,
        min_attempts: 2,
      },
    };
  },
  computed: {
    ...mapGetters(['antifrode_signupAttempts', 'antifrode_loading']),
    attempts() {
      return this.antifrode_signupAttempts || [];
    },
    loading() {
      return this.antifrode_loading;
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.$store.dispatch('fetchAntifrodeSignupAttempts', {
        window_hours: this.filters.window_hours,
        min_attempts: this.filters.min_attempts,
        limit: 50,
      });
    },
    // ── helpers UI ────────────────────────────────────────────────
    attemptsBadgeColor(n) {
      if (n >= 10) return 'bg-red-600';
      if (n >= 5) return 'bg-orange-500';
      if (n >= 3) return 'bg-yellow-500';
      return 'bg-gray-400';
    },
    parseProfileTypes(str) {
      if (!str) return [];
      return str.split(',').filter(Boolean);
    },
    profileChipClass(type) {
      return {
        FOREIGN: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        PRIVATE: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
        BUSINESS: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      }[type] || 'bg-gray-100 text-gray-500';
    },
    relativeDate(dateStr) {
      if (!dateStr) return '—';
      const diffMs = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMs / 3600000);
      const days = Math.floor(diffMs / 86400000);
      if (mins < 2) return 'adesso';
      if (mins < 60) return `${mins} min fa`;
      if (hours < 24) return `${hours}h fa`;
      if (days < 30) return `${days} gg fa`;
      return `${Math.floor(days / 30)} mesi fa`;
    },
  },
};
</script>

<style></style>
